import { Campus } from '@/modules/core/data/types/Campus';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { SerializableStudentSolutionContext } from '@/modules/core/data/types/SerializableStudentSolutionContext';
import { SerializedRecord } from '@/modules/core/data/types/SerializedRecord';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import isArray from 'lodash.isarray';
import isEmpty from 'lodash.isempty';
import map from 'lodash.map';
import size from 'lodash.size';

import {
  deserializeRecord,
  serializeRecord,
} from '../core/data/record';
import { SerializedStudentSolutionContext } from '../core/data/types/SerializedStudentSolutionContext';
import { StudentSolutionContext } from '../core/data/types/StudentSolutionContext';
import { doesEducationalProgramContainsAnyStep } from './educationalProgramStep';

/**
 * Transforms a StudentSolutionContext into a SerializedStudentSolutionContext, so that it can be forwarded on the network
 * with minimal identifiable properties, thus ensuring it can be reconstituted eventually.
 *
 * Used when sharing a simulation as link, so that it contains minimal data within the link (avoids HTTP error 431).
 * Removes empty arrays.
 *
 * @param {StudentSolutionContext} studentSolutionContext
 * @return {SerializedStudentSolutionContext}
 */
export const serializeStudentSolutionContext = (studentSolutionContext: SerializableStudentSolutionContext): SerializedStudentSolutionContext => {
  const serialized = {
    ...studentSolutionContext, // Automatically handle non-complex data types (booleans, strings, numbers, etc.)
    campuses: map(studentSolutionContext?.campuses, (record: Campus) => {
      return serializeRecord(record);
    }),
    educationalPrograms: map(studentSolutionContext?.educationalPrograms, (record: EducationalProgram) => {
      return serializeRecord(record);
    }),
    educationalProgramSteps: map(studentSolutionContext?.educationalProgramSteps, (record: EducationalProgramStep) => {
      return serializeRecord(record);
    }),
    categories: map(studentSolutionContext?.categories, (record: StudentSolutionsCategory) => {
      return serializeRecord(record);
    }),
  };

  // XXX Remove useless fields to reduce the size of the query parameters - They're useless because we can resolve them without embedding them
  delete serialized.customer;

  // Remove empty arrays to reduce the size of the query parameters
  map(serialized, (item: any, key: string) => {
    if (isArray(item) && isEmpty(item)) {
      delete serialized[key];
    }
  });

  return serialized;
};

/**
 * Deserializes a SerializedStudentSolutionContext into a StudentSolutionContext
 *
 * Also ensures to overwrite any empty array to "undefined" to avoid mismatch when matching solutions with the context
 *
 * @param {SerializedStudentSolutionContext} serializedStudentSolutionContext
 * @param {Campus[]} campuses
 * @param {EducationalProgram[]} educationalPrograms
 * @param {EducationalProgramStep[]} educationalProgramSteps
 * @param {StudentSolutionsCategory[]} categories
 * @return {StudentSolutionContext}
 */
export const deserializeStudentSolutionContext = (
  serializedStudentSolutionContext: SerializedStudentSolutionContext,
  campuses: Campus[],
  educationalPrograms: EducationalProgram[],
  educationalProgramSteps: EducationalProgramStep[],
  categories: StudentSolutionsCategory[],
): StudentSolutionContext => {
  const customerRef = process.env.NEXT_PUBLIC_CUSTOMER_REF;

  if (!size(customerRef)) {
    throw new Error(`NEXT_PUBLIC_CUSTOMER_REF is not defined. (using "${process.env.NEXT_PUBLIC_CUSTOMER_REF}")`);
  }

  // XXX When an array is empty, convert it to "undefined"
  //  Having an empty array in the context will apply the filter for that field, which isn't what we want
  //  (We only want to take a filter into account if a value is actually set)
  const serializedCampuses = serializedStudentSolutionContext?.campuses
    ?.filter((serializedCampus: SerializedRecord) => serializedCampus?.trim()?.length > 0)?.length > 0
    ? serializedStudentSolutionContext?.campuses : [];
  const serializedEducationalPrograms = serializedStudentSolutionContext?.educationalPrograms
    ?.filter((serializedEducationalProgram: SerializedRecord) => serializedEducationalProgram?.trim()?.length > 0)?.length > 0
    ? serializedStudentSolutionContext?.educationalPrograms : [];
  const serializedEducationalProgramSteps = serializedStudentSolutionContext?.educationalProgramSteps
    ?.filter((serializedEducationalProgramStep: SerializedRecord) => serializedEducationalProgramStep?.trim()?.length > 0)?.length > 0
    ? serializedStudentSolutionContext?.educationalProgramSteps : [];
  const serializedCategories = serializedStudentSolutionContext?.categories
    ?.filter((serializedCategory: SerializedRecord) => serializedCategory?.trim()?.length > 0)?.length > 0
    ? serializedStudentSolutionContext?.categories : [];

  return {
    ...serializedStudentSolutionContext, // Automatically handle non-complex data types (booleans, strings, numbers, etc.)
    customer: { // XXX Resolve from env var because it's not embedded within the serialised context
      ref: customerRef,
    },
    campuses: size(serializedCampuses) ? map(serializedCampuses, (record: SerializedRecord) => {
      return deserializeRecord(record, campuses);
    }) : undefined,
    educationalPrograms: size(serializedEducationalPrograms) ? map(serializedEducationalPrograms, (record: SerializedRecord) => {
      return deserializeRecord(record, educationalPrograms);
    }) : undefined,
    educationalProgramSteps: size(serializedEducationalProgramSteps) ? map(serializedEducationalProgramSteps, (record: SerializedRecord) => {
      return deserializeRecord(record, educationalProgramSteps);
    }) : undefined,
    categories: size(serializedCategories) ? map(serializedCategories, (record: SerializedRecord) => {
      return deserializeRecord(record, categories);
    }) : undefined,
  };
};

/**
 * A context may contain educationalPrograms that aren't actually used (no related educationalProgramStep)
 *
 * Those programs are "ghosts". They exist, but they aren't really used. And sometimes, it causes the app to function unexpectedly.
 * This helper is meant to filter out such ghost programs so that they don't temper with the expected behaviour.
 *
 * @param {StudentSolutionContext} studentSolutionContext
 * @return {StudentSolutionContext}
 */
export const filterUnusedContextEducationalPrograms = (studentSolutionContext: StudentSolutionContext): StudentSolutionContext => {
  const educationalPrograms = studentSolutionContext?.educationalPrograms || [];
  const educationalProgramSteps = studentSolutionContext?.educationalProgramSteps || [];
  const usedEducationalPrograms = [];

  map(educationalPrograms, (educationalProgram: EducationalProgram) => {
    if (doesEducationalProgramContainsAnyStep(educationalProgram, educationalProgramSteps)) {
      usedEducationalPrograms.push(educationalProgram);
    }
  });

  return {
    ...studentSolutionContext,
    educationalPrograms: usedEducationalPrograms, // Replace current educationalPrograms in the context by those that are actually used
  };
};
